import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as FilterActions from './filter.actions';
import { catchError, concatMap, map, of, switchMap, withLatestFrom } from 'rxjs';
import { TableColumnConfiguration, UserPreferencesService } from '../user-preferences/user-preferences.service';
import { Store } from '@ngrx/store';
import { getFilterState } from './filter.selector';
import { FilterStorageState, TableContext } from './filter.reducer';

type UpdatedConfigDTO = {
  updatedTableConfig: TableColumnConfiguration[];
  context: TableContext;
};

@Injectable()
export class FilterEffects {
  private actions$ = inject(Actions);
  private userPreferencesService = inject(UserPreferencesService);
  private store = inject(Store);

  initHiddenColumns$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.initHiddenColumns),
      switchMap(() => this.userPreferencesService.getHiddenColumnsConfiguration()),
      catchError(() => of([])),
      map(hiddenColumnsConfiguration => {
        const hiddenColumnsConfig = hiddenColumnsConfiguration.reduce(
          (acc, cur) => {
            acc[cur.tableContext] = cur.hiddenColumns;
            return acc;
          },
          {} as Record<string, string[]>
        );
        return FilterActions.initHiddenColumnsSuccess({ hiddenColumnsConfig });
      })
    )
  );

  updateHiddenColumns$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.updateHiddenColumns),
      withLatestFrom(this.store.select(getFilterState)),
      map(
        ([updatedTableColumnConfiguration, currentState]: [
          TableColumnConfiguration,
          Record<TableContext, FilterStorageState>,
        ]) => this.updateStateWithNewColumnConfigurationForContext(currentState, updatedTableColumnConfiguration)
      ),
      concatMap(({ updatedTableConfig, context }: UpdatedConfigDTO) => {
        return this.userPreferencesService
          .updateHiddenColumnsConfiguration(updatedTableConfig)
          .pipe(
            map(
              persistedConfiguration =>
                <TableColumnConfiguration>(
                  persistedConfiguration.find(columnConfig => columnConfig.tableContext === context)
                )
            )
          );
      }),
      map(updatedTableConfiguration => FilterActions.updateHiddenColumnsSuccess(updatedTableConfiguration))
    )
  );

  private updateStateWithNewColumnConfigurationForContext(
    currentState: Record<TableContext, FilterStorageState>,
    updatedTableColumnConfiguration: TableColumnConfiguration
  ): UpdatedConfigDTO {
    const currentTableColumnConfiguration = (<TableContext[]>Object.keys(currentState)).map(key => ({
      tableContext: key,
      hiddenColumns: currentState[key].hiddenColumns ?? [],
    }));
    const combinedConfigurations = [...currentTableColumnConfiguration, updatedTableColumnConfiguration];

    return {
      updatedTableConfig: Array.from(
        new Map(combinedConfigurations.map(config => [config.tableContext, config])).values()
      ),
      context: updatedTableColumnConfiguration.tableContext,
    };
  }
}
